import type { DrawingDocument } from '../model';

// UI action rules (docs/ui-action-rules.md) Rule 4.4.1: when the drawn bonding on an atom
// exceeds its neutral valence, PeruseLab fills in the formal charge that makes it consistent --
// N with 4 bonds shows N+, O with 3 shows O+, a 2-bonded Cl shows Cl+ (chloronium). See
// docs/rule-4-4-1-auto-valence-charge.md.
//
// Covers nitrogen, oxygen, and the halogens: each has exactly one neutral valence, so
// over-bonding can only be resolved by a positive charge. Carbon (cation / anion / radical all
// plausible), sulfur and phosphorus (several neutral valences) are deliberately excluded --
// their genuinely invalid cases stay with chemicalValidation's MAX_VALENCE ceiling.
const NEUTRAL_VALENCE: Readonly<Record<number, number>> = { 7: 3, 8: 2, 9: 1, 17: 1, 35: 1, 53: 1 };

/**
 * The formal charge Rule 4.4.1 would assign to an N / O atom carrying `bondOrderSum` bonds:
 * `bondOrderSum - neutralValence` when over-bonded (clamped to +2), else 0. Under-bonded atoms
 * get 0 here -- that case is covered by implicit hydrogens, never an auto negative charge.
 */
export function autoValenceCharge(atomicNumber: number, bondOrderSum: number): number {
  const neutral = NEUTRAL_VALENCE[atomicNumber];
  if (neutral === undefined || bondOrderSum <= neutral) return 0;
  return Math.min(2, bondOrderSum - neutral);
}

function bondOrderSumByNodeId(document: DrawingDocument): Record<string, number> {
  const sums: Record<string, number> = Object.fromEntries(document.structure.graph.nodes.map((node) => [node.id, 0]));
  for (const edge of document.structure.graph.edges) {
    const bond = document.structure.bonds[edge.id];
    const order = bond?.order ?? 1;
    // #1037: a dative bond counts toward neither atom. Its charges are implied by the bond itself
    // (N->O is N+-O-), so they never belong in the stored formal charges. Counting it toward the
    // acceptor auto-charged an impossible O->NR3 into a plausible-looking NR3+, hiding the
    // over-valent N from chemicalValidation.
    if (bond?.type === 'dative') continue;
    sums[edge.source] = (sums[edge.source] ?? 0) + order;
    sums[edge.target] = (sums[edge.target] ?? 0) + order;
  }
  return sums;
}

/**
 * Rule 4.4.1 reconcile pass. Runs at the tail of every structural mutator that can change an
 * N / O atom's element or bonding. For each N / O node whose stored charge is 0, or was itself
 * auto-assigned (it matches `autoValenceCharge` for that atom's bonding in `previous`), it resets
 * `formalCharges[id]` to `autoValenceCharge` for the *new* bonding. A charge the user set by hand
 * -- anything that doesn't match the auto rule -- is left untouched. Pure and idempotent; not
 * called from undo/redo, which restore an already-reconciled document.
 */
export function reconcileValenceCharges(next: DrawingDocument, previous: DrawingDocument): DrawingDocument {
  const previousSums = bondOrderSumByNodeId(previous);
  const nextSums = bondOrderSumByNodeId(next);
  let changed = false;
  const formalCharges = { ...next.structure.formalCharges };

  for (const node of next.structure.graph.nodes) {
    const atom = next.structure.atoms[node.id];
    if (!atom || NEUTRAL_VALENCE[atom.atomicNumber] === undefined) continue;

    const current = formalCharges[node.id] ?? 0;
    const previousAtom = previous.structure.atoms[node.id];
    const wasAuto =
      current !== 0 &&
      previousAtom !== undefined &&
      autoValenceCharge(previousAtom.atomicNumber, previousSums[node.id] ?? 0) === current;
    if (current !== 0 && !wasAuto) continue; // a hand-set charge -- leave it

    const target = autoValenceCharge(atom.atomicNumber, nextSums[node.id] ?? 0);
    if (target !== current) {
      formalCharges[node.id] = target;
      changed = true;
    }
  }

  if (!changed) return next;
  return { ...next, structure: { ...next.structure, formalCharges } };
}
